import { useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { 
  LayoutDashboard, Users, Package, ShoppingCart, 
  Settings, LogOut, Menu, X, FileText, CreditCard, Receipt, MessageSquare, ShieldCheck, BookOpen, BarChart3, Layout, ShoppingBag
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/analytics', label: 'Analys', icon: BarChart3 },
    { path: '/admin/home-builder', label: 'Startsida-byggare', icon: Layout },
    { path: '/admin/orders', label: 'Beställningar', icon: ShoppingCart },
    { path: '/admin/products', label: 'Produkter', icon: Package },
    { path: '/admin/merchant-feed', label: 'Google Merchant', icon: ShoppingBag },
    { path: '/admin/catalogs', label: 'Katalog', icon: BookOpen },
    { path: '/admin/users', label: 'Användare', icon: Users },
    { path: '/admin/content', label: 'Innehåll', icon: FileText },
    { path: '/admin/payments', label: 'Betalningar', icon: CreditCard },
    { path: '/admin/settings', label: 'Inställningar', icon: Settings },
    { path: '/admin/tax', label: 'Skatt', icon: Receipt },
    { path: '/admin/reviews', label: 'Recensioner', icon: MessageSquare },
    { path: '/admin/security', label: 'Säkerhet', icon: ShieldCheck },
  ];

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside 
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-slate-900 text-white transition-all duration-300 flex flex-col fixed h-full z-50`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <img 
                src="/logo.jpg" 
                alt="Printsout" 
                className="h-10 w-auto"
              />
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-primary text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-slate-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Logga ut</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
